import Content, { ContentNS } from "content/content";
import DatePicker from "features/shared/components/date-picker/DatePicker";
import SubmitButton from "features/shared/components/buttons/submit-button/SubmitButton";
import useConversionAnalytics from "features/shared/hooks/analytics/useConversionAnalytics";
import useMutatedPagePath from "features/shared/hooks/analytics/useMutatedPagePath";
import { getGeneralAnalyticsParams } from "features/shared/hooks/analytics/useGeneralAnalytics";
import { getPaxLevelParams } from "features/shared/hooks/analytics/usePaxLevelAnalytics";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import useItemLevelNavigationState, {
  ItemNavigationState,
} from "features/shared/hooks/navigation/useItemLevelNavigationState";
import usePaxSession from "features/shared/hooks/usePaxSession";
import Logging from "logging";
import { FC, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DataProvider from "services/data-provider/data-provider";
import { useAppStore } from "store/store";
import { useShallow } from "zustand/shallow";
import {
  ApplyDocumentErrorWrapper,
  ApplyDocumentErrorRulesMismatch,
} from "services/data-provider/ApplyDocumentResponse";
import DataProviderGeneralException, {
  DataProviderExceptionType,
} from "services/data-provider/data-provider-errors";
import SessionIneligibilityReason, {
  SessionIneligibilityReasonFromApi,
} from "models/session-ineligibility-reason";
import { DocProcessingStatus } from "models/doc-processing-status";
import AnalyticsService from "services/analytics/analytics";
import { LocalDate } from "utils";
import Body from "features/shared/styles/typography/Body.styled";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const OnwardTicketForm: FC = () => {
  const textDocs = (s: string) => Content.shared.t(s, { ns: ContentNS.docs });

  const [searchParams] = useSearchParams();
  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  const navigate = useNavigateWithQuery();
  const paxSession = usePaxSession();
  const [path, title, location] = useMutatedPagePath();
  const sendConversionEvents = useConversionAnalytics();

  const [
    sessionId,
    locale,
    paxSessions,
    journey,
    setProcessingStatus,
    setProcessedFileName,
    setProcessingMayTakeLong,
    setIneligibilityReason,
    setSessionIsLocked,
  ] = useAppStore(
    useShallow((state) => [
      state.sessionId,
      state.locale,
      state.paxSessions,
      state.journey,
      state.setProcessingStatus,
      state.setProcessedFileName,
      state.setProcessingMayTakeLong,
      state.setIneligibilityReason,
      state.setSessionIsLocked,
    ])
  );

  const itemType =
    itemLevelNavigationState instanceof ItemNavigationState
      ? itemLevelNavigationState.item.itemType
      : "unknown";

  const analyticsParams = {
    ...getGeneralAnalyticsParams(
      paxSessions,
      journey,
      path,
      title,
      location,
      locale
    ),
    ...getPaxLevelParams(paxSessions, paxSession?.paxSessionId || null),
    item_type: itemType,
    is_scan: false,
    is_upload: false,
    is_manual: true,
  };

  const [travelDate, setTravelDate] = useState("");
  const [travelDateError, setTravelDateError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    AnalyticsService.shared.trackEvent(
      "manual_selected",
      analyticsParams,
      false // isScanRelated
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = (): boolean => {
    if (!travelDate) {
      setTravelDateError(textDocs("onward-ticket.error-travel-date-required"));
      return false;
    }
    setTravelDateError(null);
    return true;
  };

  const onSubmit = () => {
    if (isLoading) return;
    if (!validate()) return;

    const paxSessionId = paxSession?.paxSessionId;
    if (!paxSessionId) return;

    setIsLoading(true);

    setProcessingStatus(DocProcessingStatus.InProgress, null);
    setProcessedFileName(null);
    setProcessingMayTakeLong(null);
    navigate("../processing");

    AnalyticsService.shared.trackEvent(
      "apply_started",
      analyticsParams,
      false // isScanRelated
    );

    DataProvider.shared
      .applyManualDocument(sessionId, paxSessionId, {
        type: "onward-ticket",
        travelDate,
      })
      .then(async (res) => {
        const previousPaxSessions = useAppStore.getState().paxSessions;

        if (res.updatedPaxSession) {
          useAppStore.getState().updatePaxSession(res.updatedPaxSession);
        }

        if (!res.successfullyVerified) {
          // API accepted the manual input and updated the session, but the
          // checklist item still requires verification (e.g. rules mismatch
          // that leaves the item in verification-required status).
          setProcessingStatus(
            DocProcessingStatus.Error,
            "verification_required"
          );
          return;
        }

        setProcessingStatus(DocProcessingStatus.Success, null);

        AnalyticsService.shared.trackEvent(
          "apply_success",
          analyticsParams,
          false // isScanRelated
        );

        await delay(100);

        sendConversionEvents(
          previousPaxSessions,
          useAppStore.getState().paxSessions
        );
      })
      .catch((err) => {
        if (err instanceof ApplyDocumentErrorWrapper) {
          if (err.error instanceof ApplyDocumentErrorRulesMismatch) {
            setProcessingStatus(DocProcessingStatus.Error, err.error);

            AnalyticsService.shared.trackEvent(
              "apply_failed",
              {
                ...analyticsParams,
                failed_to_process: 0,
                id_mismatch_error: 0,
                rules_mismatch_error: 1,
              },
              false // isScanRelated
            );
            return;
          }

          setProcessingStatus(DocProcessingStatus.Error, err.error);
          return;
        }

        if (
          err instanceof DataProviderGeneralException &&
          err.type !== DataProviderExceptionType.Unknown &&
          err.type !== DataProviderExceptionType.BadRequest &&
          err.type !== DataProviderExceptionType.NotFound
        ) {
          switch (err.type) {
            case DataProviderExceptionType.NotProcessable:
            case DataProviderExceptionType.NotEligible:
              setIneligibilityReason(
                SessionIneligibilityReasonFromApi(err.message)
              );
              setProcessingStatus(DocProcessingStatus.Ineligible, null);

              AnalyticsService.shared.trackEvent(
                "apply_ineligible",
                {
                  ...analyticsParams,
                  failed_to_process: 1,
                  id_mismatch_error: 0,
                  rules_mismatch_error: 0,
                },
                false // isScanRelated
              );
              return;

            case DataProviderExceptionType.SessionIsFinalised:
              setIneligibilityReason(
                SessionIneligibilityReason.SessionIsFinalised
              );
              return;

            case DataProviderExceptionType.SessionIsLocked:
              setSessionIsLocked(true);
              return;
          }
        }

        Logging.log(
          "manual-input",
          paxSessionId,
          `Failed to apply manual document: ${
            err instanceof Error ? err.message : err
          }`
        );
        setProcessingStatus(DocProcessingStatus.Error, "unknown_error");

        AnalyticsService.shared.trackEvent(
          "apply_failed",
          {
            ...analyticsParams,
            failed_to_process: 1,
            id_mismatch_error: 0,
            rules_mismatch_error: 0,
          },
          false // isScanRelated
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <DatePicker
        id="onward-ticket-travel-date"
        value={travelDate}
        label={textDocs("onward-ticket.travel-date-label")}
        error={travelDateError}
        onChange={(value) => setTravelDate(value)}
        min={LocalDate.fromDateInLocalTimezone(new Date()).toIsoString()}
      />
      <Body>{textDocs("onward-ticket.legal-disclaimer")}</Body>
      <SubmitButton onClick={onSubmit} isLoading={isLoading}>
        {textDocs("onward-ticket.submit")}
      </SubmitButton>
    </>
  );
};

export default OnwardTicketForm;
